import React, { useState } from "react";
import {
  BadgeCheck, Plus, Pencil, Trash2, ExternalLink, ShieldCheck,
  GraduationCap, Loader2, FileText, ClipboardCheck,
} from "lucide-react";
import {
  AppBadge, AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  AppModal, Callout, ConfirmModal, EmptyState, FormField, IconButton,
  Input,
} from "../ui";
import NavChip from "./NavChip";
import { fmtDate } from "./constants";
import { certificationsApi, uploadFile, errorText } from "./portfolioApi";

/**
 * CertificationsSection — two lists that look alike and are not.
 *
 * ⚠️ EARNED HERE vs ADDED BY THE STUDENT. The first block is read live from the
 * LMS's own `Certificate` and assessment records: the institute issued them, they
 * carry a certificate number and a verification URL, and a student cannot edit
 * them. The second is what the student typed in about credentials earned
 * elsewhere. Presenting the two identically would let a self-declared "AWS
 * Certified" sit beside an institute-issued certificate with the same weight,
 * which is precisely the claim a recruiter is reading this to check.
 */

const EMPTY = {
  name: "", issuer: "", issueDate: "", expiryDate: "",
  credentialId: "", credentialUrl: "", fileKey: "",
};

const toForm = (c) => ({
  name: c.name || "",
  issuer: c.issuer || "",
  issueDate: c.issueDate ? c.issueDate.slice(0, 10) : "",
  expiryDate: c.expiryDate ? c.expiryDate.slice(0, 10) : "",
  credentialId: c.credentialId || "",
  credentialUrl: c.credentialUrl || "",
  fileKey: c.fileKey || "",
});

const isExpired = (c) => c.expiryDate && new Date(c.expiryDate) < new Date();

const CertificationsSection = ({
  certifications = [], earned = { certificates: [], quizzes: [] },
  onPortfolioChange, readOnly = false,
}) => {
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const del = async () => {
    setBusy(true);
    try {
      const { portfolio } = await certificationsApi.remove(confirm._id);
      onPortfolioChange(portfolio, "Certification removed.");
      setConfirm(null);
    } catch (err) {
      onPortfolioChange(null, errorText(err, "Failed to remove the certification."), "danger");
    } finally {
      setBusy(false);
    }
  };

  const lmsCerts = earned?.certificates || [];
  const quizzes = earned?.quizzes || [];
  const passedQuizzes = quizzes.filter((q) => q.passed);

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {/* ── Earned here. Read-only by construction. ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle className="[&_svg]:tw-text-success">
            <ShieldCheck size={18} /> Earned at this institute
          </AppCardTitle>
          <p className="tw-m-0 tw-mt-1 tw-text-[0.8rem] tw-leading-relaxed tw-text-content-muted">
            Certificates issued by your institute and the assessments you have passed. These are
            verified records and cannot be edited.
          </p>
        </AppCardHeader>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-5">
          {lmsCerts.length === 0 && quizzes.length === 0 ? (
            <EmptyState
              icon={GraduationCap}
              title="Nothing earned yet"
              description="Course certificates and passed assessments will appear here automatically."
            />
          ) : (
            <>
              {lmsCerts.length > 0 && (
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <SubHeading icon={GraduationCap}>Course certificates</SubHeading>
                  <ul className="tw-m-0 tw-grid tw-list-none tw-gap-2 tw-p-0 min-[760px]:tw-grid-cols-2">
                    {lmsCerts.map((c) => (
                      <li
                        key={c.id}
                        className="tw-flex tw-items-start tw-gap-3 tw-rounded-sm tw-border tw-border-solid tw-border-success/25 tw-bg-success/[0.06] tw-p-3.5"
                      >
                        <span className="tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-sm tw-bg-success/15 tw-text-success">
                          <BadgeCheck size={17} />
                        </span>
                        <div className="tw-min-w-0 tw-flex-1">
                          <h5 className="tw-m-0 tw-truncate tw-text-[0.9rem] tw-font-semibold tw-text-content">
                            {c.courseName}
                          </h5>
                          <p className="tw-m-0 tw-mt-0.5 tw-text-xs tw-text-content-subtle">
                            {c.certificateNo} · {fmtDate(c.issueDate)}
                            {c.quizScore !== null && c.quizScore !== undefined && ` · ${c.quizScore}%`}
                          </p>
                          <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-gap-2">
                            {c.fileUrl && (
                              <NavChip shape="chip" size="xs" href={c.fileUrl} icon={FileText}>Certificate</NavChip>
                            )}
                            {c.verifyUrl && (
                              <NavChip shape="chip" size="xs" href={c.verifyUrl} icon={ShieldCheck}>Verify</NavChip>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {quizzes.length > 0 && (
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <SubHeading icon={ClipboardCheck}>
                    Assessments
                    <span className="tw-ml-1.5 tw-font-normal tw-text-content-subtle">
                      {passedQuizzes.length} of {quizzes.length} passed
                    </span>
                  </SubHeading>
                  <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1.5 tw-p-0">
                    {quizzes.map((q) => (
                      <li
                        key={q.id}
                        className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-rounded-sm tw-border tw-border-solid tw-border-line-soft tw-px-3 tw-py-2"
                      >
                        <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-[0.85rem] tw-font-medium tw-text-content">
                          {q.title}
                        </span>
                        <span className={`tw-text-sm tw-font-semibold tw-tabular-nums ${q.passed ? "tw-text-success" : "tw-text-content-muted"}`}>
                          {Math.round(q.percentage ?? 0)}%
                        </span>
                        <AppBadge color={q.passed ? "emerald" : undefined}
                          variant={q.passed ? "default" : "secondary"} size="sm">
                          {q.passed ? "Passed" : "Not passed"}
                        </AppBadge>
                        <span className="tw-text-xs tw-text-content-subtle">{fmtDate(q.submittedAt)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </AppCardContent>
      </AppCard>

      {/* ── Added by the student. ── */}
      <AppCard>
        <AppCardHeader className="tw-flex-row tw-items-start tw-justify-between tw-gap-3">
          <div className="tw-min-w-0">
            <AppCardTitle className="[&_svg]:tw-text-primary">
              <BadgeCheck size={18} /> Other certifications
              {certifications.length > 0 && (
                <span className="tw-ml-1 tw-text-sm tw-font-normal tw-text-content-subtle">
                  ({certifications.length})
                </span>
              )}
            </AppCardTitle>
            <p className="tw-m-0 tw-mt-1 tw-text-[0.8rem] tw-leading-relaxed tw-text-content-muted">
              Credentials you earned elsewhere — AWS, Google, Coursera, a university course.
            </p>
          </div>
          {!readOnly && (
            <AppButton variant="outline" size="sm" onClick={() => setModal({ mode: "add" })}>
              <Plus size={14} /> Add
            </AppButton>
          )}
        </AppCardHeader>

        <AppCardContent>
          {certifications.length === 0 ? (
            <EmptyState
              icon={BadgeCheck}
              title="No external certifications"
              description={readOnly
                ? "This student has not added any external certifications."
                : "Add certifications you earned outside this institute. A credential link makes them checkable."}
              action={!readOnly && (
                <AppButton size="sm" onClick={() => setModal({ mode: "add" })}>
                  <Plus size={14} /> Add a certification
                </AppButton>
              )}
            />
          ) : (
            <ul className="tw-m-0 tw-grid tw-list-none tw-gap-2 tw-p-0 min-[760px]:tw-grid-cols-2">
              {certifications.map((c) => (
                <li
                  key={c._id}
                  className="tw-flex tw-items-start tw-gap-3 tw-rounded-sm tw-border tw-border-solid tw-border-line-soft tw-bg-surface-sunken tw-p-3.5"
                >
                  <span className="tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-sm tw-bg-primary/10 tw-text-primary">
                    <BadgeCheck size={17} />
                  </span>
                  <div className="tw-min-w-0 tw-flex-1">
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                      <h5 className="tw-m-0 tw-text-[0.9rem] tw-font-semibold tw-text-content">{c.name}</h5>
                      {isExpired(c) && <AppBadge variant="secondary" size="sm">Expired</AppBadge>}
                    </div>
                    <p className="tw-m-0 tw-mt-0.5 tw-text-xs tw-text-content-subtle">
                      {[
                        c.issuer,
                        c.issueDate && fmtDate(c.issueDate),
                        c.expiryDate && `expires ${fmtDate(c.expiryDate)}`,
                      ].filter(Boolean).join(" · ") || "—"}
                    </p>
                    {c.credentialId && (
                      <p className="tw-m-0 tw-mt-0.5 tw-text-xs tw-text-content-subtle">ID {c.credentialId}</p>
                    )}
                    <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-gap-2">
                      {c.credentialUrl && <NavChip shape="chip" size="xs" href={c.credentialUrl} icon={ExternalLink}>Verify</NavChip>}
                      {c.fileUrl && <NavChip shape="chip" size="xs" href={c.fileUrl} icon={FileText}>Certificate</NavChip>}
                    </div>
                  </div>
                  {!readOnly && (
                    <div className="tw-flex tw-shrink-0 tw-gap-0.5">
                      <IconButton label="Edit certification" icon={Pencil} size="sm"
                        onClick={() => setModal({ mode: "edit", item: c })} />
                      <IconButton label="Remove certification" icon={Trash2} size="sm"
                        variant="ghost-destructive" onClick={() => setConfirm(c)} />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </AppCardContent>

        {modal && (
          <CertificationModal
            mode={modal.mode}
            item={modal.item}
            onClose={() => setModal(null)}
            onSaved={(portfolio, message) => { onPortfolioChange(portfolio, message); setModal(null); }}
          />
        )}

        <ConfirmModal
          show={!!confirm}
          onHide={() => !busy && setConfirm(null)}
          onConfirm={del}
          loading={busy}
          variant="danger"
          title="Remove this certification?"
          message={confirm ? `"${confirm.name}" will be removed from your portfolio.` : ""}
          confirmText="Remove"
        />
      </AppCard>
    </div>
  );
};

const SubHeading = ({ icon: Icon, children }) => (
  <h4 className="tw-m-0 tw-flex tw-items-center tw-gap-1.5 tw-text-[0.7rem] tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-subtle">
    <Icon size={13} /> {children}
  </h4>
);

const CertificationModal = ({ mode, item, onClose, onSaved }) => {
  const [form, setForm] = useState(() => (item ? toForm(item) : EMPTY));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError("The file must be under 10 MB."); return; }
    setUploading(true);
    setError("");
    try {
      const { key } = await uploadFile(file);
      set({ fileKey: key });
    } catch (err) {
      setError(errorText(err, "Failed to upload the file."));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("A certification name is required."); return; }
    setSaving(true);
    setError("");
    try {
      const { portfolio, message } = mode === "add"
        ? await certificationsApi.add(form)
        : await certificationsApi.update(item._id, form);
      onSaved(portfolio, message);
    } catch (err) {
      setError(errorText(err, "Failed to save the certification."));
      setSaving(false);
    }
  };

  return (
    <AppModal
      show
      onHide={onClose}
      title={mode === "add" ? "Add a certification" : "Edit certification"}
      icon={<BadgeCheck size={18} />}
      footer={
        <div className="tw-flex tw-justify-end tw-gap-2">
          <AppButton variant="secondary" onClick={onClose} disabled={saving}>Cancel</AppButton>
          <AppButton onClick={submit} disabled={saving || uploading}>
            {saving ? "Saving…" : "Save"}
          </AppButton>
        </div>
      }
    >
      <form className="tw-flex tw-flex-col tw-gap-4" onSubmit={submit}>
        {error && <Callout tone="danger" role="alert">{error}</Callout>}

        <FormField label="Certification name" required>
          <Input value={form.name} onChange={(e) => set({ name: e.target.value })}
            placeholder="e.g. AWS Certified Cloud Practitioner" maxLength={200} autoFocus />
        </FormField>

        <FormField label="Issued by">
          <Input value={form.issuer} onChange={(e) => set({ issuer: e.target.value })}
            placeholder="Amazon Web Services" maxLength={160} />
        </FormField>

        <div className="tw-grid tw-gap-4 min-[560px]:tw-grid-cols-2">
          <FormField label="Issued on">
            <Input type="date" value={form.issueDate} onChange={(e) => set({ issueDate: e.target.value })} />
          </FormField>
          <FormField label="Expires on" hint="Leave blank if it does not expire">
            <Input type="date" value={form.expiryDate} onChange={(e) => set({ expiryDate: e.target.value })} />
          </FormField>
        </div>

        <div className="tw-grid tw-gap-4 min-[560px]:tw-grid-cols-2">
          <FormField label="Credential ID">
            <Input value={form.credentialId} onChange={(e) => set({ credentialId: e.target.value })}
              maxLength={120} />
          </FormField>
          <FormField label="Credential URL" hint="The issuer's verification page">
            <Input type="url" value={form.credentialUrl}
              onChange={(e) => set({ credentialUrl: e.target.value })} placeholder="https://…" />
          </FormField>
        </div>

        <FormField label="Certificate file" hint="Optional — a PDF or image copy">
          <div className="tw-flex tw-items-center tw-gap-2">
            <input type="file" accept="image/*,application/pdf" onChange={onFile}
              className="tw-text-xs tw-text-content-muted file:tw-mr-2 file:tw-cursor-pointer file:tw-rounded-md file:tw-border-0 file:tw-bg-surface-hover file:tw-px-3 file:tw-py-1.5 file:tw-text-xs file:tw-font-medium file:tw-text-content" />
            {uploading && <Loader2 size={15} className="tw-animate-spin tw-text-primary" />}
            {form.fileKey && !uploading && (
              <span className="tw-text-xs tw-font-medium tw-text-success">File attached</span>
            )}
          </div>
        </FormField>
      </form>
    </AppModal>
  );
};

export default CertificationsSection;
